import { defaultLinkStyle } from "components/ui/link";
import { Spinner } from "components/ui/spinner";
import * as React from "react";
import { Link } from "react-router-dom";
import { NO_NAME } from "util/text";
import type { PipelineStatusDate } from "./pipeline-status-date";
import { badgeColorForNextTask, NextActionLine, NextTaskLike } from "./task-badge";

export interface HomeCareLeadCardModel {
    href: string | null;
    name: string | null;
    bestContactName: string | null;
    nextTask: NextTaskLike | null;
    assignedUserName: string | null;
    status: PipelineStatusDate;
}

interface HomeCareLeadCardProps {
    lead: HomeCareLeadCardModel;
    highlightCard: boolean;
    pendingCard: boolean;
    showAssignedTo: boolean;
}

const Names: React.FC<{ lead: HomeCareLeadCardModel }> = ({ lead }) => (
    <div className="flex-1">
        <div className="font-bold">{lead.name || NO_NAME}</div>
        {lead.bestContactName ? (
            <div className="text-xs text-gray-600">{lead.bestContactName}</div>
        ) : null}
    </div>
);

const TaskDot: React.FC<{ lead: HomeCareLeadCardModel; pendingCard: boolean }> = ({
    lead,
    pendingCard,
}) => (
    <div className="flex-none">
        {pendingCard ? (
            <Spinner />
        ) : (
            <div
                data-testid="next-task-dot"
                className={`${badgeColorForNextTask(lead.nextTask)} rounded-full w-3 h-3`}
            />
        )}
    </div>
);

const Title: React.FC<{ lead: HomeCareLeadCardModel; pendingCard: boolean }> = props => {
    const { lead } = props;
    const content = (
        <div className="flex gap-1 justify-between">
            <Names lead={lead} />
            <TaskDot {...props} />
        </div>
    );
    return lead.href ? (
        <Link to={lead.href} className={defaultLinkStyle}>
            {content}
        </Link>
    ) : (
        content
    );
};

const StatusDetail: React.FC<{ status: PipelineStatusDate }> = ({ status }) => (
    <div className="text-xs">
        {status.descriptor}:{" "}
        {status.date.map(x => x.humanFriendly()).getOrElse("(not specified)")}
    </div>
);

export const HomeCareLeadCard: React.FC<HomeCareLeadCardProps> = props => {
    const { lead, highlightCard, pendingCard, showAssignedTo } = props;
    return (
        <div
            className={`space-y-2 pipeline-card pipeline-client-card ${
                highlightCard ? "highlight-card" : ""
            }`}>
            <div>
                <Title lead={lead} pendingCard={pendingCard} />
            </div>
            <NextActionLine nextTask={lead.nextTask} />
            <StatusDetail status={lead.status} />
            {showAssignedTo ? (
                <p className="text-xs italic">{lead.assignedUserName ?? "Unassigned"}</p>
            ) : null}
        </div>
    );
};
